import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { Button } from '../../../../../../../ui';
import { JobPostingTabData } from '../../../../../../../utils/types/JobOpenings';

interface UnifiedActionsProps {
  resolvedPostId?: string;
  isSaving: boolean;
  jobPostingData: JobPostingTabData;
  onSaveOrCreate: () => void;
}

export const UnifiedActions = ({
  resolvedPostId,
  isSaving,
  jobPostingData,
  onSaveOrCreate,
}: UnifiedActionsProps) => {
  const { t } = useTranslation(NS.adminPages);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/jobs/${resolvedPostId}`);
    toast.success(t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.urlCopied) || 'Link copied!', {
      position: 'bottom-center',
    });
  };

  return (
    <div className='rounded-xl border border-emerald-200 bg-white p-4 shadow-md'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h3 className='text-base font-extrabold text-slate-900'>
            { resolvedPostId
              ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updateJobPost) || 'Update Job Post'
              : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.publishJob) || 'Publish Job Post' }
          </h3>
          <p className='text-sm text-slate-500'>
            { resolvedPostId
              ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updateSubtitle) || 'Save edits to the public site'
              : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.publishSubtitle) || 'Launch your job posting online' }
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-3 w-full sm:w-auto'>
          {resolvedPostId && (
            <button
                onClick={handleCopyLink}
                className='flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-slate-700 text-sm font-semibold'
            >
              <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' />
              </svg>
              Copy Link
            </button>
          )}
          <Button
              onClick={onSaveOrCreate}
              variant='primary'
              label={
                isSaving
                  ? resolvedPostId
                    ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updating) || 'Updating...'
                    : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.creating) || 'Creating...'
                  : resolvedPostId
                    ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updateButton) || 'Update Post'
                    : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.createButton) || 'Create Post'
              }
              className='w-full sm:w-auto font-semibold'
              disabled={
                isSaving ||
                !jobPostingData.jobTitle ||
                !jobPostingData.department ||
                !jobPostingData.location
              }
          />
        </div>
      </div>
    </div>
  );
};

export default UnifiedActions;
